import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import DynamicLeaguePage from "./DynamicLeaguePage";
import MyPageBanner from "../components/MyPageBanner";
import StateMessage from "../components/StateMessage";
import field1 from "../images/field1.png";
import "./LeaguePage.css";

/**
 * Loads a published league by URL id, then renders the league page.
 * Unpublished / unknown ids get a calm empty state (not a crash).
 */
const LeaguePage = ({ bannerImage = field1 }) => {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      setLeague(null);

      if (!leagueId) {
        setStatus("missing");
        return;
      }

      try {
        const { data, error } = await supabase
          .from("leagues")
          .select(
            `
            id,
            league_name,
            theme_color,
            track_players,
            track_goals,
            finals_started,
            season,
            archived
          `
          )
          .eq("id", leagueId)
          .eq("is_published", true)
          .maybeSingle();

        if (cancelled) return;

        if (error) throw error;

        if (!data) {
          setStatus("missing");
          return;
        }

        setLeague({
          ...data,
          league_id: data.id,
        });
        setStatus("ready");
      } catch (err) {
        console.error("Error loading league:", err);
        if (!cancelled) setStatus("missing");
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  if (status === "loading") {
    return (
      <>
        <MyPageBanner imageUrl={bannerImage} text={"\u00a0"} />
        <div className="league-gate league-gate--loading">
          <StateMessage kind="loading" plain>
            Cargando liga…
          </StateMessage>
        </div>
      </>
    );
  }

  if (status === "missing" || !league) {
    return (
      <div className="league-gate">
        <div className="league-gate-card">
          <p className="league-gate-eyebrow">Liga</p>
          <h1 className="league-gate-title">Próximamente</h1>
          <p className="league-gate-copy">
            Esta liga aún no está disponible o no existe.
          </p>
          <Link to="/ligas" className="league-gate-link">
            Ver ligas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <DynamicLeaguePage
      bannerImage={bannerImage}
      bannerText={league.league_name}
      bannerSubtitle={league.season ? `Temporada ${league.season}` : null}
      bannerMeta={
        league.archived ? (
          <span className="banner-pill">Temporada archivada</span>
        ) : league.finals_started ? (
          <span className="banner-pill banner-pill--accent">Finales en curso</span>
        ) : null
      }
      leagueName={league.league_id}
      themeColor={league.theme_color}
      trackPlayers={league.track_players}
      trackGoals={league.track_goals}
      finalsStarted={league.finals_started}
    />
  );
};

export default LeaguePage;
